import { useState } from 'react';
import { KeyValueList, type KeyValueRow } from '@/components/key-value-list';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { paymentMethodLabel } from '../model/billing-status';
import type { WorkspaceData } from '../types';
import { BillingEmailForm } from './billing-email-form';

type PaymentDetailsCardProps = {
  workspace: WorkspaceData;
  isUpdating: boolean;
  onUpdatePaymentMethod: () => void;
};

export function PaymentDetailsCard({
  workspace,
  isUpdating,
  onUpdatePaymentMethod,
}: PaymentDetailsCardProps) {
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const { billing } = workspace;
  const isPaid = billing.subscription !== null;
  const isPastDue = billing.subscription?.status === 'PAST_DUE';
  const canEdit = isPaid && billing.paddle !== null;

  const rows: KeyValueRow[] = [
    {
      key: 'method',
      label: 'Payment method',
      valueClassName: 'flex flex-wrap items-center justify-between gap-3',
      value: (
        <>
          <span className="inline-flex items-center gap-2">
            {paymentMethodLabel(billing.paymentMethod)}
            {isPastDue ? <Pill tone="bad">Declined</Pill> : null}
          </span>
          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              isLoading={isUpdating}
              onClick={onUpdatePaymentMethod}
            >
              Update
            </Button>
          ) : null}
        </>
      ),
    },
    {
      key: 'email',
      label: 'Billing email',
      valueClassName: 'flex flex-wrap items-center justify-between gap-3',
      value: isEditingEmail ? (
        <BillingEmailForm
          currentEmail={billing.billingEmail}
          onDone={() => setIsEditingEmail(false)}
        />
      ) : (
        <>
          <span>{billing.billingEmail}</span>
          {canEdit ? (
            <Button variant="ghost" size="sm" onClick={() => setIsEditingEmail(true)}>
              Edit
            </Button>
          ) : null}
        </>
      ),
    },
    {
      key: 'tax',
      label: 'Address and tax ID',
      value: 'Kept by Paddle, shown on each invoice',
      valueClassName: 'text-muted',
    },
  ];

  return (
    <Card asChild>
      <section data-enter="" aria-labelledby="pay-h" className="px-5 py-2">
        <h2 id="pay-h" className="sr-only">
          Payment details
        </h2>
        <KeyValueList rows={rows} labelClassName="text-muted" />
      </section>
    </Card>
  );
}
